import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, X as XIcon, Check } from 'lucide-react';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

const HomePage = () => {
  // Six risk categories the platform scores every report against —
  // sourced from backend/models/Report.js riskBreakdown schema.
  const riskCategories = [
    'Financial',
    'Legal',
    'Operational',
    'Reputation',
    'Jurisdiction',
    'Security'
  ];

  // Before/After contrast — directionally truthful comparisons of the
  // typical alternative approach vs id100. Avoids specific time/cost
  // claims that would be hard to substantiate.
  const comparisonRows = [
    { before: 'Days of manual research across multiple sites',  after: 'Structured intelligence in minutes' },
    { before: 'Inconsistent depth and quality',                 after: 'Six-category risk scoring, every report' },
    { before: 'Hourly advisory fees per engagement',            after: 'Predictable, flat platform pricing' },
    { before: 'Five siloed data sources to reconcile',          after: 'One platform, one report, one view' },
    { before: '"Further investigation recommended"',            after: 'Clear red, amber, green risk flags' },
    { before: 'Reports tracked manually in spreadsheets',       after: 'Every report stored, searchable, downloadable' }
  ];

  const pricingPlans = [
    {
      name: 'Single Report',
      price: '£29',
      period: 'one-time',
      description: 'Perfect for occasional due diligence needs',
      features: [
        '1 comprehensive report',
        '1 page summary plus full report',
        'AI-powered analysis',
        'View online or export',
        'Available for 30 days'
      ],
      cta: 'Run a Report'
    },
    {
      name: 'Essential',
      price: '£150',
      period: 'per month',
      description: 'For businesses requiring regular checks',
      features: [
        '10 reports per month',
        'Priority processing',
        'All export formats',
        'Email support'
      ],
      featured: true,
      cta: 'Start Subscription'
    },
    {
      name: 'Professional',
      price: '£499',
      period: 'per month',
      description: 'For high-volume requirements',
      features: [
        '50 reports per month',
        'Fastest processing',
        'API access',
        'Dedicated support'
      ],
      cta: 'Start Subscription'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="id100 — AI-powered due diligence reports"
        description="Generate verified investigative reports on companies and individuals. AI-powered, source-cited, blockchain-audited."
        path="/"
      />
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Logo size="md" />
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/pricing"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Pricing
              </Link>
              <Link
                to="/auth"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Sign In
              </Link>
              <Link
                to="/auth"
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Professional Due Diligence
              <span className="block text-red-600">In Minutes, Not Days</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Structured intelligence on any company &mdash; six-category risk scoring, source-cited, returned in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/auth"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Buy a Report
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Six Risk Categories Strip */}
      <div className="bg-gray-900 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-400 text-xs sm:text-sm font-semibold mb-6 uppercase tracking-widest">
            Every report scored across six categories
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {riskCategories.map((cat) => (
              <div
                key={cat}
                className="bg-gray-800 border border-gray-700 rounded-lg py-3 px-4 text-center"
              >
                <span className="text-white font-medium text-sm sm:text-base">{cat}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Before / After Contrast Section */}
      <div className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              From days of patchwork research to minutes of structured intelligence
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              How due diligence on id100 compares to the typical patchwork approach most teams settle for.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 sm:px-6 py-4 text-left text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider w-1/2">
                    Before id100
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs sm:text-sm font-semibold text-red-600 uppercase tracking-wider w-1/2">
                    After id100
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {comparisonRows.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 sm:px-6 py-4 text-gray-600 text-sm sm:text-base align-top">
                      <span className="inline-flex items-start gap-2">
                        <XIcon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                        <span>{row.before}</span>
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium text-sm sm:text-base align-top">
                      <span className="inline-flex items-start gap-2">
                        <Check className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />
                        <span>{row.after}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              See pricing
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Pricing Preview Section */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your needs. All plans include full access to our AI-powered analysis.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-lg p-8 ${
                  plan.featured
                    ? 'bg-red-600 text-white shadow-xl scale-105'
                    : 'bg-white border border-gray-200'
                }`}
              >
                <h3 className={`text-2xl font-bold mb-2 ${plan.featured ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className={`text-4xl font-bold ${plan.featured ? 'text-white' : 'text-gray-900'}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ml-2 ${plan.featured ? 'text-red-100' : 'text-gray-600'}`}>
                    {plan.period}
                  </span>
                </div>
                <p className={`mb-6 ${plan.featured ? 'text-red-100' : 'text-gray-600'}`}>
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className={`h-5 w-5 mr-2 flex-shrink-0 ${
                        plan.featured ? 'text-white' : 'text-red-600'
                      }`} />
                      <span className={plan.featured ? 'text-white' : 'text-gray-700'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/auth"
                  className={`block w-full text-center px-6 py-3 rounded-md font-medium transition-colors ${
                    plan.featured
                      ? 'bg-white text-red-600 hover:bg-gray-100'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {plan.cta || 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-red-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Run your next report on id100
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Backed by Totalinfo Ltd &mdash; 26 years of professional intelligence work.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-red-600 transition-colors"
          >
            Create Account
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="mb-4">
                <Logo size="sm" />
              </div>
              <p className="text-sm text-gray-400">
                Professional due diligence reports powered by AI technology.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/auth" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link></li>
                <li>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('id100:open-cookie-prefs'))}
                    className="hover:text-white transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                  >Cookie preferences</button>
                </li>
                <li><Link to="/data-request" className="hover:text-white transition-colors">Data subject request</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:info@totalinfo.net" className="hover:text-white transition-colors">
                    info@totalinfo.net
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Totalinfo Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
